/**
 * SafetyShieldTest — Public test page for the Zéro Relais feature.
 *
 * Accessible WITHOUT authentication at `/safety-test` so Yuri (and any
 * external tester) can validate:
 *   • Geo-localized emergency resources per country
 *   • Legal shield modal UX
 *   • Distress keyword detection (live preview)
 *
 * External shareable link:
 *   {REACT_APP_BACKEND_URL}/safety-test
 */
import React, { useEffect, useState } from 'react';
import { ShieldCheck, AlertTriangle, Globe2, Search, PlayCircle, CheckCircle2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import LegalShieldModal from '../components/LegalShieldModal';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const COUNTRIES = [
  { code: 'CA', name: '🇨🇦 Canada' },
  { code: 'US', name: '🇺🇸 United States' },
  { code: 'FR', name: '🇫🇷 France' },
  { code: 'BE', name: '🇧🇪 Belgique' },
  { code: 'CH', name: '🇨🇭 Suisse' },
  { code: 'GB', name: '🇬🇧 United Kingdom' },
  { code: 'AU', name: '🇦🇺 Australia' },
  { code: 'NZ', name: '🇳🇿 New Zealand' },
  { code: 'DE', name: '🇩🇪 Deutschland' },
  { code: 'ES', name: '🇪🇸 España' },
  { code: 'IT', name: '🇮🇹 Italia' },
  { code: 'PT', name: '🇵🇹 Portugal' },
  { code: 'JP', name: '🇯🇵 日本' },
];

const EXAMPLE_PHRASES = [
  { label: "💤 Fatigue banale", text: "J'ai mal dormi et je suis fatigue aujourd'hui.", expectedCrisis: false },
  { label: "😥 Démotivation", text: "Je suis demotive et j'ai craque sur du chocolat.", expectedCrisis: false },
  { label: "🚨 Détresse suicidaire", text: "Je n'ai plus envie de vivre, j'ai des idees noires.", expectedCrisis: true },
  { label: "🚨 Automutilation", text: "J'ai envie de me couper pour me punir.", expectedCrisis: true },
  { label: "🚨 Urgence médicale", text: "Mon ami ne respire plus, je pense qu'il fait une crise cardiaque.", expectedCrisis: true },
  { label: "🚨 Violence conjugale", text: "Mon conjoint me frappe regulierement.", expectedCrisis: true },
];

export default function SafetyShieldTest() {
  const [country, setCountry] = useState('CA');
  const [language, setLanguage] = useState('fr');
  const [health, setHealth] = useState(null);
  const [resources, setResources] = useState(null);
  const [loadingRes, setLoadingRes] = useState(false);
  const [shieldOpen, setShieldOpen] = useState(false);
  const [customText, setCustomText] = useState('');
  const [detection, setDetection] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND}/api/safety/health`)
      .then((r) => r.json())
      .then(setHealth)
      .catch((e) => setHealth({ ok: false, error: e.message }));
  }, []);

  const loadResources = async () => {
    setLoadingRes(true);
    try {
      const res = await fetch(
        `${BACKEND}/api/safety/public-resources?country=${country}&language=${language}`
      );
      const data = await res.json();
      setResources(data);
    } catch (e) {
      setResources({ error: e.message });
    } finally {
      setLoadingRes(false);
    }
  };

  useEffect(() => {
    loadResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country, language]);

  const runDetection = (text) => {
    // Client-side mirror of backend keyword set (FR + EN) for instant preview.
    // Source of truth remains backend — this is UX sugar.
    // Defensive coerce — sometimes `text` is an object pulled from form state
    // (e.g. a Select option object). `(text || '').toLowerCase()` would crash
    // with "(e||'').toLowerCase is not a function". `String(x ?? '')` is safe.
    const lower = String(text ?? '').toLowerCase();
    const CRISIS = [
      'suicide', 'suicidaire', 'me tuer', 'en finir', 'plus envie de vivre',
      'idees noires', 'idées noires', 'je veux mourir',
      'automutilation', 'me couper', 'scarifier', 'scarification',
      'anorexie', 'boulimie', 'me faire vomir', 'binge eating',
      'crise cardiaque', 'avc', 'overdose', 'convulsions', 'ne respire plus',
      'violence conjugale', 'il me bat', 'elle me bat', 'abus sexuel',
      'voix dans ma tete', 'voix dans ma tête', 'hallucination',
      'kill myself', 'end my life', 'want to die', 'self-harm', 'self harm',
      'heart attack', 'stroke', 'not breathing', 'domestic violence',
    ];
    const hits = CRISIS.filter((k) => lower.includes(k));
    return { hits, isCrisis: hits.length > 0 };
  };

  const handleCustomDetect = () => {
    const d = runDetection(customText);
    setDetection(d);
    if (d.isCrisis) setShieldOpen(true);
  };

  const triggerExample = (phrase) => {
    setCustomText(phrase.text);
    const d = runDetection(phrase.text);
    setDetection(d);
    if (d.isCrisis) setShieldOpen(true);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-950 via-neutral-900 to-black text-white">
      <div className="max-w-4xl mx-auto px-4 py-8 md:py-12">
        {/* Header */}
        <header className="mb-8 md:mb-10" data-testid="safety-test-header">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2.5 bg-red-500/15 border border-red-500/30 rounded-xl">
              <ShieldCheck className="w-6 h-6 text-red-400" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
              YUMIX Safety Shield — Test Public
            </h1>
          </div>
          <p className="text-neutral-400 text-sm md:text-base leading-relaxed max-w-2xl">
            Page externe de validation du <strong className="text-white">Bouclier Légal</strong> YUMIX (Zéro Relais).
            Simule la détection de détresse, valide les ressources d'urgence géo-localisées
            et inspecte le flux légal. Aucune authentification requise.
          </p>
        </header>

        {/* Health status */}
        <section className="mb-8 grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="bg-neutral-900/70 border border-neutral-800 rounded-xl p-4" data-testid="safety-health-card">
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Backend Status</div>
            {health?.ok ? (
              <div className="flex items-center gap-2 text-emerald-400 font-semibold">
                <CheckCircle2 className="w-4 h-4" /> En ligne
              </div>
            ) : (
              <div className="flex items-center gap-2 text-red-400 font-semibold">
                <AlertTriangle className="w-4 h-4" /> Indisponible
              </div>
            )}
          </div>
          <div className="bg-neutral-900/70 border border-neutral-800 rounded-xl p-4">
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">API ThroughLine</div>
            <div className={health?.api_enabled ? 'text-emerald-400 font-semibold' : 'text-amber-400 font-semibold'}>
              {health?.api_enabled ? 'Connectée' : 'Fallback (numéros curatés)'}
            </div>
          </div>
          <div className="bg-neutral-900/70 border border-neutral-800 rounded-xl p-4">
            <div className="text-xs uppercase tracking-wider text-neutral-500 mb-1">Pays supportés (statique)</div>
            <div className="text-white font-semibold">
              {health?.supported_countries_static?.length || 0} pays
            </div>
          </div>
        </section>

        {/* 1. Country & Language selector */}
        <section className="mb-8" data-testid="safety-country-selector-section">
          <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2 mb-3">
            <Globe2 className="w-4 h-4 text-red-400" /> 1. Simuler un pays / une langue
          </h2>
          <div className="flex flex-wrap gap-3 mb-3">
            <select
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              className="bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 text-sm"
              data-testid="safety-country-select"
            >
              {COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>{c.name}</option>
              ))}
            </select>
            <select
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
              className="bg-neutral-900 border border-neutral-700 rounded-lg px-3 py-2 text-sm"
              data-testid="safety-language-select"
            >
              <option value="fr">Français</option>
              <option value="en">English</option>
            </select>
            <Button
              onClick={() => setShieldOpen(true)}
              className="bg-red-600 hover:bg-red-500 rounded-lg"
              data-testid="safety-open-shield-btn"
            >
              <PlayCircle className="w-4 h-4 mr-2" />
              Ouvrir le Bouclier Légal
            </Button>
          </div>

          {loadingRes && <p className="text-neutral-500 text-sm">Chargement des ressources…</p>}
          {resources && !resources.error && (
            <div className="bg-neutral-900/70 border border-neutral-800 rounded-xl p-4" data-testid="safety-resources-preview">
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                  <div className="text-sm text-neutral-500">Source : </div>
                  <div className="font-semibold">
                    {resources.source === 'api' ? '🌐 ThroughLine API' : '📚 Fallback curaté'}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-neutral-500">Pays : </div>
                  <div className="font-semibold">{resources.country_name} ({resources.country})</div>
                </div>
              </div>
              <ul className="space-y-2">
                {resources.resources?.map((r, i) => (
                  <li key={i} className="flex items-baseline gap-2 text-sm">
                    <span className="text-neutral-500 w-40 shrink-0 truncate">{r.type}</span>
                    <span className="text-white font-semibold">{r.number}</span>
                    <span className="text-neutral-400 truncate">— {r.label}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </section>

        {/* 2. Distress keyword detection */}
        <section className="mb-8" data-testid="safety-detection-section">
          <h2 className="text-lg font-semibold tracking-tight flex items-center gap-2 mb-3">
            <Search className="w-4 h-4 text-red-400" /> 2. Simuler la détection de détresse
          </h2>
          <p className="text-neutral-400 text-sm mb-3">
            Saisis une phrase ou clique sur un exemple. Le bouclier s'ouvre automatiquement
            si des mots-clés de détresse sont détectés.
          </p>

          <div className="flex gap-2 mb-4">
            <Input
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Ex: J'ai des idees noires depuis quelques jours..."
              className="bg-neutral-900 border-neutral-700 text-white"
              data-testid="safety-detection-input"
            />
            <Button
              onClick={handleCustomDetect}
              className="bg-red-600 hover:bg-red-500"
              data-testid="safety-detection-run-btn"
            >
              Tester
            </Button>
          </div>

          {detection && (
            <div
              className={`mb-4 p-3 rounded-lg text-sm ${
                detection.isCrisis
                  ? 'bg-red-500/15 border border-red-500/40 text-red-200'
                  : 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200'
              }`}
              data-testid="safety-detection-result"
            >
              {detection.isCrisis ? (
                <>🚨 <strong>Détresse détectée.</strong> Mots-clés : {detection.hits.join(', ')}</>
              ) : (
                <>✅ Aucun mot-clé de détresse détecté.</>
              )}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {EXAMPLE_PHRASES.map((p, i) => (
              <button
                key={i}
                onClick={() => triggerExample(p)}
                className={`text-left p-3 rounded-xl border transition text-sm ${
                  p.expectedCrisis
                    ? 'bg-red-500/5 border-red-500/30 hover:bg-red-500/10'
                    : 'bg-neutral-900 border-neutral-800 hover:bg-neutral-800'
                }`}
                data-testid={`safety-example-${i}`}
              >
                <div className="font-semibold mb-1">{p.label}</div>
                <div className="text-neutral-400 italic">"{p.text}"</div>
              </button>
            ))}
          </div>
        </section>

        {/* Footer */}
        <footer className="mt-10 pt-6 border-t border-neutral-800 text-xs text-neutral-500 space-y-1">
          <p>
            <strong className="text-neutral-300">Architecture :</strong> Géolocalisation dynamique →
            API ThroughLine (findahelpline.com) en priorité, fallback sur numéros officiels pré-validés par pays.
          </p>
          <p>
            <strong className="text-neutral-300">Auditabilité :</strong> Chaque redirection vers une ligne
            d'aide est journalisée dans <code className="text-neutral-400">db.safety_redirections</code>
            (user_id hashé, aucun texte brut).
          </p>
          <p>
            <strong className="text-neutral-300">Zéro hallucination :</strong> Aucun numéro n'est généré
            par l'IA. Si ThroughLine est injoignable, fallback sur 112/911/988/etc.
          </p>
        </footer>
      </div>

      <LegalShieldModal
        open={shieldOpen}
        onClose={() => setShieldOpen(false)}
        onAcknowledge={() => {}}
        country={country}
        language={language}
        publicMode
        trigger="public_test"
      />
    </div>
  );
}
